/**
 * Migrated Junto Pay API v2 Integration
 * Updated to use the new junto-pay-v2 backend endpoints
 */

// Configuration - Update to point to new backend
const API_BASE_URL = process.env.REACT_APP_JUNTO_API_URL || 'http://localhost:8000/api/v2';

// Token management - Clerk tokens only
let clerkToken = null;

// Reference data cache
let referenceDataCache = {
  instructors: null,
  locations: null,
  rooms: null,
  categories: null,
  lastUpdated: null,
  isStale: () => {
    if (!referenceDataCache.lastUpdated) return true;
    const oneHour = 60 * 60 * 1000;
    return Date.now() - referenceDataCache.lastUpdated > oneHour;
  }
};

// API Client with Clerk authentication
class JuntoAPIClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.getTokenFunction = null; // Will be set by auth context
    this.authReadyPromise = null; // Promise that resolves when auth is ready
    this.authReadyResolve = null; // Function to resolve the promise
    
    // Create the auth ready promise
    this.authReadyPromise = new Promise((resolve) => {
      this.authReadyResolve = resolve;
    });
  }

  setGetTokenFunction(getTokenFn) {
    this.getTokenFunction = getTokenFn;
  }
  
  markAuthReady() {
    if (this.authReadyResolve) {
      this.authReadyResolve();
    }
  }

  async request(endpoint, options = {}) {
    // Wait for auth to be ready (this is instant if already ready)
    await this.authReadyPromise;
    
    // ALWAYS try to get a fresh token if we have a token getter function
    // This ensures authenticated users are tracked on ALL endpoints
    if (this.getTokenFunction) {
      try {
        const freshToken = await this.getTokenFunction();
        if (freshToken && freshToken !== clerkToken) {
          clerkToken = freshToken;
        }
      } catch (error) {
        // Silently continue - user might not be authenticated
        // We still want to make the request, just without auth
      }
    }
    // Ensure endpoint has a trailing slash
    let normalizedEndpoint = endpoint;
    if (!normalizedEndpoint.endsWith('/') && !normalizedEndpoint.includes('?')) {
      normalizedEndpoint += '/';
    } else if (normalizedEndpoint.includes('?') && !normalizedEndpoint.split('?')[0].endsWith('/')) {
      const [path, query] = normalizedEndpoint.split('?');
      normalizedEndpoint = `${path}/?${query}`;
    }
    
    const url = `${this.baseURL}${normalizedEndpoint}`;
    const config = {
      headers: {
        'Content-Type': 'application/json',
        // Add current page URL as a custom header
        'X-Page-URL': window.location.href,
        'X-Page-Path': window.location.pathname,
        ...options.headers,
      },
      ...options,
    };

    // Add Clerk token if available
    if (clerkToken) {
      config.headers.Authorization = `Bearer ${clerkToken}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `Request failed: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${endpoint}`, error);
      throw error;
    }
  }

  setClerkToken(token) {
    clerkToken = token;
  }

  clearAuth() {
    clerkToken = null;
  }
}

const apiClient = new JuntoAPIClient();

// Reference Data Management with Parallel Loading
export const referenceDataService = {
  // Add a promise to track when auth is ready
  authReadyPromise: null,
  
  setAuthReady(promise) {
    this.authReadyPromise = promise;
  },
  
  async loadAllReferenceData() {
    try {
      // If we have an auth ready promise, wait for it
      if (this.authReadyPromise) {
        try {
          await this.authReadyPromise;
        } catch (e) {
          // Auth failed, but we can still load public data
        }
      }
      
      // Make all reference data requests in parallel - Updated endpoints
      const [instructors, locations, categories] = await Promise.all([
        apiClient.request('/users/instructors/'),
        apiClient.request('/locations/'),
        apiClient.request('/classes/categories/'),
      ]);

      // Get all rooms from all locations
      const roomPromises = locations.map(location => 
        apiClient.request(`/locations/${location.id}/rooms/`)
      );
      const roomsArrays = await Promise.all(roomPromises);
      const rooms = roomsArrays.flat();

      // Cache the results
      referenceDataCache = {
        instructors,
        locations,
        rooms,
        categories,
        lastUpdated: Date.now(),
        isStale: () => {
          const oneHour = 60 * 60 * 1000;
          return Date.now() - referenceDataCache.lastUpdated > oneHour;
        }
      };

      return referenceDataCache;
    } catch (error) {
      console.error('Failed to load reference data:', error);
      throw error;
    }
  },

  async getReferenceData() {
    if (referenceDataCache.isStale()) {
      await this.loadAllReferenceData();
    }
    return referenceDataCache;
  },

  async getInstructors() {
    const data = await this.getReferenceData();
    return data.instructors;
  },

  async getLocations() {
    const data = await this.getReferenceData();
    return data.locations;
  },

  async getRooms() {
    const data = await this.getReferenceData();
    return data.rooms;
  },

  async getCategories() {
    const data = await this.getReferenceData();
    return data.categories;
  }
};

// Authentication Service - Updated for Clerk only
export const authService = {
  async login() {
    throw new Error('Login is handled by Clerk. This method should not be called.');
  },

  async signup() {
    throw new Error('Signup is handled by Clerk. This method should not be called.');
  },

  async logout() {
    apiClient.clearAuth();
    // Clerk handles the actual logout
  },

  async refreshToken() {
    throw new Error('Token refresh is handled by Clerk. This method should not be called.');
  },

  async forgotPassword() {
    throw new Error('Password reset is handled by Clerk. This method should not be called.');
  },

  async getProfile() {
    return await apiClient.request('/auth/profile/');
  },

  setClerkToken(token) {
    apiClient.setClerkToken(token);
  }
};

// Class Service - Updated endpoints and parameters
export const classService = {
  async getClasses(filters = {}) {
    // Map frontend parameters to backend parameters
    const mappedFilters = { ...filters };
    
    if (filters.days) {
      mappedFilters.days_ahead = filters.days;
      delete mappedFilters.days;
    }
    
    // Note: 'search' parameter is not supported in the new API
    if (filters.search) {
      console.warn('Text search is not supported in the new API - ignoring search parameter');
      delete mappedFilters.search;
    }
    
    // Build query params - location_id is now supported directly
    const queryParams = new URLSearchParams();
    Object.entries(mappedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, value);
      }
    });
    
    const queryString = queryParams.toString();
    const endpoint = queryString ? `/classes/instances/?${queryString}` : '/classes/instances/';
    
    return await apiClient.request(endpoint);
  },

  async getClass(id) {
    return await apiClient.request(`/classes/instances/${id}/`);
  },

  async validateBooking(data) {
    // This endpoint doesn't exist in the new API - needs implementation
    console.error('Booking validation endpoint not implemented in new API');
    throw new Error('Booking validation not available');
  }
};

// Booking Service - Updated endpoints
export const bookingService = {
  async createBooking(data) {
    return await apiClient.request('/bookings/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async cancelBooking(id) {
    return await apiClient.request(`/bookings/${id}/cancel/`, {
      method: 'PUT'
    });
  },

  async getBookingHistory(userId) {
    // This endpoint doesn't exist in the new API - needs implementation
    console.error('Booking history endpoint not implemented in new API');
    throw new Error('Booking history not available');
  }
};

// User Service - Updated endpoints
export const userService = {
  async getUser(id) {
    return await apiClient.request(`/users/${id}/`);
  },

  async updateUser(id, data) {
    return await apiClient.request(`/users/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async getUserWallet(id) {
    // This endpoint doesn't exist in the new API - needs implementation
    console.error('User wallet endpoint not implemented in new API');
    throw new Error('User wallet not available');
  },

  async getUserMemberships(id) {
    // This endpoint doesn't exist in the new API - needs implementation
    console.error('User memberships endpoint not implemented in new API');
    throw new Error('User memberships not available');
  },

  async purchaseMembership(userId, membershipId, data) {
    // This endpoint doesn't exist in the new API - needs implementation
    console.error('Membership purchase endpoint not implemented in new API');
    throw new Error('Membership purchase not available');
  },

  async purchasePackage(userId, data) {
    // This endpoint doesn't exist in the new API - needs implementation
    console.error('Package purchase endpoint not implemented in new API');
    throw new Error('Package purchase not available');
  }
};

// Payment Service - All endpoints need implementation
export const paymentService = {
  async addPaymentMethod(data) {
    console.error('Payment method endpoints not implemented in new API');
    throw new Error('Payment methods not available');
  },

  async removePaymentMethod(id) {
    console.error('Payment method endpoints not implemented in new API');
    throw new Error('Payment methods not available');
  },

  async setDefaultPaymentMethod(id) {
    console.error('Payment method endpoints not implemented in new API');
    throw new Error('Payment methods not available');
  },

  async processStaxPayment(data) {
    console.error('Stax payment processing not implemented in new API');
    throw new Error('Payment processing not available');
  },

  async getTransactions() {
    // The new API has a transactions endpoint but with different schema
    return await apiClient.request('/transactions/');
  }
};

// Membership/Credit Service - Updated endpoints
export const membershipService = {
  async getMemberships() {
    return await apiClient.request('/purchasable/memberships/');
  },

  async getMembership(id) {
    return await apiClient.request(`/purchasable/memberships/${id}/`);
  },

  async getCreditPackages() {
    return await apiClient.request('/purchasable/credit-packages/');
  },

  async getCredits() {
    return await apiClient.request('/purchasable/credits/');
  },
  async getCategories() {
    return await apiClient.request('/purchasable/categories/');
  },
  async getPurchasableEntities() {
    return await apiClient.request('/purchasable/purchasable-entities/');
  }
};

// Store Service - All endpoints need implementation
export const storeService = {
  async getStoreItems(filters = {}) {
    console.error('Store endpoints not implemented in new API');
    throw new Error('Store not available');
  },

  async getStoreItem(id) {
    console.error('Store endpoints not implemented in new API');
    throw new Error('Store not available');
  },

  async purchaseStoreItem(id, data) {
    console.error('Store endpoints not implemented in new API');
    throw new Error('Store not available');
  }
};

// Cart/Promo Service - Needs implementation
export const cartService = {
  async validateCart(data) {
    console.error('Cart validation endpoint not implemented in new API');
    throw new Error('Cart validation not available');
  }
};

// Export all services
export const juntoAPI = {
  authService,
  referenceDataService,
  classService,
  bookingService,
  userService,
  paymentService,
  membershipService,
  storeService,
  cartService,
  
  // Utility method to set Clerk token
  setClerkToken: (token) => {
    apiClient.setClerkToken(token);
  },
  
  // Method to register token getter function
  setGetTokenFunction: (getTokenFn) => {
    apiClient.setGetTokenFunction(getTokenFn);
  },
  
  // Method to mark auth as ready
  markAuthReady: () => {
    apiClient.markAuthReady();
  }
};

// Default export
export default juntoAPI;